import React from 'react';
import { Box, Typography, Paper, Chip, LinearProgress } from '@mui/material';
import {
  Add as CreateIcon,
  Toll as CreditIcon,
  Payments as PayoutIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: 3-step process
const StepsVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
    {[
      { num: 1, label: 'Basic Info' },
      { num: 2, label: 'Payouts' },
      { num: 3, label: 'Review' },
    ].map((step, i) => (
      <React.Fragment key={step.num}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 0.5,
          }}>
            {step.num}
          </Box>
          <Typography variant="caption" sx={{ fontSize: 10 }}>{step.label}</Typography>
        </Box>
        {i < 2 && <Box sx={{ width: 20, height: 2, bgcolor: 'grey.300' }} />}
      </React.Fragment>
    ))}
  </Box>
);

// Visual: Credit cost tiers
const CreditCostVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
    {[
      { mode: 'Single', credits: 1, desc: 'Same all game' },
      { mode: 'Half', credits: 2, desc: 'Changes at half' },
      { mode: 'Quarter', credits: 4, desc: 'Each quarter' },
    ].map((tier) => (
      <Paper key={tier.mode} elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', minWidth: 70 }}>
        <Typography variant="caption" fontWeight="bold" display="block">{tier.mode}</Typography>
        <Chip
          icon={<CreditIcon sx={{ fontSize: 14 }} />}
          label={tier.credits}
          size="small"
          color="primary"
          sx={{ mt: 0.5, mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 9 }}>
          {tier.desc}
        </Typography>
      </Paper>
    ))}
  </Box>
);

// Visual: Payout validation
const PayoutValidationVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center', maxWidth: 200, mx: 'auto' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption">Total Pot</Typography>
      <Typography variant="caption" fontWeight="bold">$1,000</Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption">Allocated</Typography>
      <Typography variant="caption" color="success.main">$1,000</Typography>
    </Box>
    <LinearProgress variant="determinate" value={100} color="success" sx={{ height: 8, borderRadius: 1, mb: 0.5 }} />
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
      <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />
      <Typography variant="caption" color="success.main">100% allocated</Typography>
    </Box>
  </Box>
);

// Visual: Template option
const TemplateOptionVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', textAlign: 'center', minWidth: 90 }}>
      <Typography variant="caption" display="block">Start Fresh</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Configure everything</Typography>
    </Paper>
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200', textAlign: 'center', minWidth: 90 }}>
      <Typography variant="caption" fontWeight="bold" color="primary.main" display="block">Use Template</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Pre-filled settings</Typography>
    </Paper>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Create a Board',
    description: 'Follow a simple 3-step wizard to create your board: enter basic info, configure payouts, then review and create.',
    icon: <CreateIcon sx={{ fontSize: 40 }} />,
    visual: <StepsVisual />,
  },
  {
    title: 'Credit Costs',
    description: 'Different number modes cost different credits. Single mode (1 credit) keeps the same numbers all game. Half and Quarter modes randomize more often.',
    icon: <CreditIcon sx={{ fontSize: 40 }} />,
    visual: <CreditCostVisual />,
  },
  {
    title: 'Payout Validation',
    description: 'The system ensures you allocate exactly 100% of the payout pot. Real-time feedback shows remaining amounts as you configure.',
    icon: <PayoutIcon sx={{ fontSize: 40 }} />,
    visual: <PayoutValidationVisual />,
  },
  {
    title: 'Use Templates',
    description: 'Have a saved template? Start from it to skip configuration. Your payout structure and settings will be pre-filled automatically.',
    icon: <CheckIcon sx={{ fontSize: 40 }} />,
    visual: <TemplateOptionVisual />,
  },
];

interface CreateBoardEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const CreateBoardEducationModal: React.FC<CreateBoardEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="create_board"
    steps={steps}
    headerColor="success.main"
    finishButtonText="Create Board"
    buttonColor="success"
  />
);

export default CreateBoardEducationModal;
